import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SchoolCostCorrectionFormComponent } from '@k12/task-forms';
import {
  SchoolRosterStudent,
  Task,
  SubTask,
  TaskTypeAction,
  StartCostCorrectionRequest,
  ToastMessageType
} from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import { ToastMessageService } from '@k12/app-services';

export interface CostCorrectionDialogData {
  student: SchoolRosterStudent;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-cost-correction-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SchoolCostCorrectionFormComponent
  ],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <h2 mat-dialog-title>Initiate Cost Correction</h2>
        <button mat-icon-button mat-dialog-close aria-label="Close dialog">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content>
        <lib-school-cost-correction-form
          [task]="mockTask"
          [taskTypeAction]="mockTaskTypeAction"
          [taskAction]="mockTaskAction"
          [isSubmitting]="isSubmitting"
          (submitFormData)="onFormSubmit($event)">
        </lib-school-cost-correction-form>
      </mat-dialog-content>
    </div>
  `,
  styles: [
    `
      .dialog-container {
        display: flex;
        flex-direction: column;
        max-height: 90vh;
      }
      .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-right: 8px;
      }
      mat-dialog-content {
        padding-top: 16px;
      }
    `
  ]
})
export class CostCorrectionDialogComponent implements OnInit {
  private readonly schoolsService = inject(SchoolsService);
  private readonly toastService = inject(ToastMessageService);
  private readonly dialogRef = inject(MatDialogRef<CostCorrectionDialogComponent>);
  public data = inject<CostCorrectionDialogData>(MAT_DIALOG_DATA);

  isSubmitting = false;

  private readonly EmptyGuid = '00000000-0000-0000-0000-000000000000';

  // Mocks to satisfy the form component's inputs
  mockTask: Task = { id: this.EmptyGuid } as Task;
  mockTaskTypeAction: TaskTypeAction = {
    id: this.EmptyGuid,
    $type: 'Form',
    description: '',
    name: '',
    sequenceNumber: 1,
    sendWeeklyReminders: false,
    sendReminder: false
  } as unknown as TaskTypeAction;
  mockTaskAction: SubTask = {
    id: this.EmptyGuid,
    taskStatusId: 1, // Active
    formData: ''
  } as SubTask;

  constructor() {}

  ngOnInit(): void {
    // Pass the student enrollment ID via the mock task action's formData
    this.mockTaskAction.formData = JSON.stringify({
      studentEnrollmentId: this.data.student.studentEnrollmentId
    });
  }

  onFormSubmit(formData: string): void {
    this.isSubmitting = true;

    const request: StartCostCorrectionRequest = {
      studentEnrollmentId: this.data.student.studentEnrollmentId,
      formData: formData
    };

    this.schoolsService.startCostCorrection(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.toastService.showToast(ToastMessageType.Success, 'Cost correction workflow initiated successfully.');
          this.dialogRef.close(true);
        } else {
          this.toastService.showToast(
            ToastMessageType.Error,
            response.message || 'Failed to initiate cost correction.'
          );
        }
        this.isSubmitting = false;
      },
      error: (error) => {
        console.error('Error initiating cost correction:', error);
        this.toastService.showToast(ToastMessageType.Error, 'An error occurred while initiating the cost correction.');
        this.isSubmitting = false;
      }
    });
  }
}
